import { Box, IconButton, Stack } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { getProjectDetails, getProjectTags, qk } from 'api'
import { IconTrashBin } from 'assets/icons'
import { Loader } from 'elements/feedback'
import { Checkbox } from 'elements/inputs'
import { MultipleAutocomplete } from 'elements/inputs/autocomplete'
import { Button } from 'elements/inputs/button'
import { Select } from 'elements/inputs/select'
import { renderTextField } from 'elements/inputs/text-field'
import { getInputError, useValidations } from 'lib/form-utils'
import { useTermDuplicates } from 'lib/hooks'
import { useEffect } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type FormValues = {
  key: string
  description: string
  tags: Array<string>
  translations: Array<{ lang: string; text: string }>
  link: boolean
}

type TermFormProps = {
  projectId: string
  defaultValues: FormValues
  onSubmit: (values: FormValues) => void
  formId: string
  resetForm?: boolean
  onReset?: () => void
}

export const TermForm = ({
  projectId,
  defaultValues,
  onSubmit,
  formId,
  resetForm,
  onReset,
}: TermFormProps) => {
  const { t } = useTranslation()

  const rules = useValidations()

  const { data } = useQuery(qk.projects.tags.toKeyWithArgs({ projectId }), () =>
    getProjectTags({ projectId }),
  )

  const tags = data ?? []

  const { data: project } = useQuery(
    qk.projects.details.toKeyWithArgs({ projectId }),
    () => getProjectDetails({ projectId }),
  )

  const { control, watch, handleSubmit, reset } = useForm<FormValues>({
    defaultValues,
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'translations',
  })

  const translationsValue = watch('translations')

  const { showDuplicatesMessages } = useTermDuplicates({
    projectId,
    translations: translationsValue,
  })

  useEffect(() => {
    if (resetForm && onReset) {
      reset()
      onReset()
    }
  }, [resetForm, reset, onReset])

  if (!project) return <Loader centered />

  const { languages } = project

  const selectedLanguages = new Set(
    translationsValue.map(translation => translation.lang),
  )

  const translationsMaxLength = project.type === 'documentation' ? 10_000 : 400

  return (
    <Box
      id={formId}
      component="form"
      onSubmit={handleSubmit(values => {
        showDuplicatesMessages()
        onSubmit(values)
      })}
    >
      <Stack spacing={3}>
        <Controller
          name="key"
          control={control}
          rules={{ ...rules.required, validate: rules.maxLength(200) }}
          render={renderTextField({
            label: t('projects.terms.term'),
          })}
        />

        <Controller
          control={control}
          name="tags"
          render={({ field, fieldState }) => (
            <MultipleAutocomplete
              enableCreatingNewOptions
              label={t('projects.terms.tags')}
              placeholder={t('projects.terms.select_tags_or_create_new')}
              {...field}
              {...getInputError(fieldState.error)}
              options={tags.map(tag => ({
                value: tag,
                label: tag,
              }))}
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          rules={{ validate: rules.maxLength(400) }}
          render={renderTextField({
            rows: 2,
            multiline: true,
            label: t('projects.terms.term_description'),
          })}
        />

        <Stack spacing={2} mb={3}>
          {fields.map((field, index) => (
            <Box display="flex" key={field.id}>
              <Box flex="0 0 190px" minWidth={0} mr={1.25} display="flex">
                <Controller
                  name={`translations.${index}.lang` as const}
                  control={control}
                  rules={rules.required}
                  render={({ field, fieldState }) => (
                    <Select
                      label={index ? undefined : t('common.language')}
                      options={languages
                        .filter(
                          language =>
                            !selectedLanguages.has(language) ||
                            language === field.value,
                        )
                        .map(languageCode => ({
                          value: languageCode,
                          label: t(`languages.${languageCode}`),
                        }))}
                      {...getInputError(fieldState.error)}
                      {...field}
                    />
                  )}
                />
              </Box>

              <Controller
                name={`translations.${index}.text` as const}
                control={control}
                rules={{
                  ...rules.required,
                  validate: rules.maxLength(translationsMaxLength),
                }}
                render={renderTextField({
                  label: index ? undefined : t('projects.terms.translation'),
                })}
              />

              <IconButton
                color="error"
                disabled={fields.length < 2}
                onClick={() => remove(index)}
                sx={{
                  height: 48,
                  px: 0.75,
                  py: 0.5,
                  mt: index === 0 ? 5 : 0,
                  ml: 1.25,
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderRadius: '4px',
                  boxShadow:
                    '0px 2px 1px rgba(0, 0, 0, 0.05), 0px 0px 1px rgba(0, 0, 0, 0.25)',
                }}
              >
                <IconTrashBin />
              </IconButton>
            </Box>
          ))}
        </Stack>

        {fields.length !== languages.length && (
          <Box>
            <Button
              variant="text"
              sx={{ color: 'primary.main' }}
              onClick={() =>
                append({
                  lang: '',
                  text: '',
                })
              }
            >
              + {t('projects.terms.add_translation')}
            </Button>
          </Box>
        )}

        <Controller
          name="link"
          control={control}
          render={({ field }) => (
            <Checkbox
              label={t('common.link')}
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Stack>
    </Box>
  )
}
